import React, { FC, useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Box,
    Chip,
    TextField,
    CircularProgress,
    Divider,
    Button,
    InputAdornment,
    IconButton,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OperatorQuery } from '../../../services/query';
import RoleApprovalCommandService from '../../../services/command/roleApproval/RoleApprovalCommand.services';
import RoleApprovalQueryService from '../../../services/query/roleApproval/RoleApprovalQuery.services';
import Swal from 'sweetalert2';
import CloseIcon from '@mui/icons-material/Close';

interface User {
    userRef: string;
    userName: string;
    fullName?: string | null;
    email?: string;
    hrref?: string;
    isActive?: boolean;
    approverUserId?: number; // Silme için gerekli
}
interface IProps {
    open: boolean;
    onClose: () => void;
    role: any;
}

export const SCUIRoleEditDialog: FC<IProps> = ({ open, onClose, role }) => {
    const { t } = useTranslation();
    const [roleManagers, setRoleManagers] = useState<User[]>([]);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingExisting, setIsLoadingExisting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [triggerRefresh, setTriggerRefresh] = useState(0);
    const [showWarning, setShowWarning] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

    const initializeModal = () => {
        if (role?.roleId) {
            setAllUsers([]);
            setSearchTerm('');
            setRoleManagers([]);
            setShowWarning(false);

            if (searchTimeout) {
                clearTimeout(searchTimeout);
                setSearchTimeout(null);
            }

            fetchExistingRoleApprovers(role.roleId);
        }
    };

    const fetchExistingRoleApprovers = async (roleId: number) => {
        setIsLoadingExisting(true);
        try {
            const response = await RoleApprovalQueryService.GetRoleApproversByRoleId(roleId);

            if (response && Array.isArray(response) && response.length > 0) {
                const existingAsUsers = response.map((approver: any) => ({
                    userRef: approver.approverUserId.toString(),
                    userName: approver.approverUserName,
                    fullName: approver.approverUserName,
                    email: '',
                    hrref: '',
                    isActive: true,
                    approverUserId: approver.approverUserId,
                }));
                setRoleManagers(existingAsUsers);
                setRefreshKey((prev) => prev + 1);
            } else {
                setRoleManagers([]);
                setRefreshKey((prev) => prev + 1);
            }
        } catch (error) {
            console.error('Error fetching existing role approvers:', error);
            setRoleManagers([]);
            setRefreshKey((prev) => prev + 1);

            Swal.fire({
                title: t('general.error'),
                text: t('roleManagement.errorLoadingRoleManagers'),
                icon: 'error',
            });
        } finally {
            setIsLoadingExisting(false);
        }
    };

    const fetchUsers = async (search?: string) => {
        setIsLoadingUsers(true);
        try {
            let response;
            if (search) {
                response = await OperatorQuery.GetOperatorFindByUserName({ userName: search });
            } else {
                response = await OperatorQuery.GetOperatorList();
            }

            if (response && response.model && response.model.data) {
                const users = response.model.data
                    .map((user: any) => ({
                        userRef: user.userRef?.toString() || user.applicationUserId?.toString() || '',
                        userName: user.username || 'Unknown',
                        fullName: user.email || null,
                        email: user.email || '',
                        hrref: user.hrref || '',
                        isActive: user.isActive || false,
                    }))
                    .filter((user: any) => user.userRef && user.userName);
                setAllUsers(users);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleSearch = () => {
        if (searchTerm.trim() && searchTerm.trim().length >= 3) {
            setShowWarning(false);
            fetchUsers(searchTerm.trim());
        } else if (searchTerm.trim().length > 0 && searchTerm.trim().length < 3) {
            setShowWarning(true);
            setAllUsers([]);
        } else {
            setShowWarning(false);
            setAllUsers([]);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleAddRoleApprover = async (user: User) => {
        try {
            const currentOperator = JSON.parse(localStorage.getItem('checkOperator') || '{}');
            const currentUserRef = currentOperator?.userRef;

            if (!currentUserRef) {
                Swal.fire({
                    title: t('common.error'),
                    text: 'Kullanıcı bilgisi bulunamadı',
                    icon: 'error',
                });
                return;
            }
            const requestData = {
                roleId: role?.roleId,
                approverUserRef: parseInt(user.userRef),
                createdBy: currentUserRef,
            };

            console.log('Adding role approver:', requestData);

            const response = await RoleApprovalCommandService.CreateRoleApprover(requestData);

            if (response) {
                console.log('🔄 Triggering refresh after add...');
                setTriggerRefresh((prev) => prev + 1);
                console.log('✅ Refresh triggered after add');

                console.log('Role approver added successfully');

                Swal.fire({
                    title: t('general.success'),
                    text: t('roleManagement.roleManagerAdded'),
                    icon: 'success',
                    timer: 2000,
                });
            }
        } catch (error) {
            console.error('Error adding role approver:', error);
            Swal.fire({
                title: t('general.error'),
                text: t('roleManagement.errorAddingRoleManager'),
                icon: 'error',
            });
        } finally {
            setAllUsers([]);
            setSearchTerm('');
            fetchExistingRoleApprovers(role?.roleId);
        }
    };

    const handleRoleManagerDelete = async (userToDelete: User) => {
        try {
            const currentOperator = JSON.parse(localStorage.getItem('checkOperator') || '{}');
            const currentUserRef = currentOperator?.userRef;

            if (!currentUserRef) {
                Swal.fire({ title: t('general.error'), text: 'Kullanıcı bilgisi bulunamadı', icon: 'error' });
                return;
            }
            if (!userToDelete.approverUserId) {
                Swal.fire({ title: t('general.error'), text: 'Rol sorumlusu ID bulunamadı', icon: 'error' });
                return;
            }

            const requestData = {
                roleApproverId: userToDelete.approverUserId,
                deletedBy: currentUserRef,
                roleId: role.roleId,
            };

            console.log('🗑️ Deleting role approver:', requestData);

            const response = await RoleApprovalCommandService.DeleteRoleApprover(requestData);

            if (response) {
                console.log('🔄 Triggering refresh after delete...');

                setRoleManagers((prevManagers) =>
                    prevManagers.filter((manager) => manager.approverUserId !== userToDelete.approverUserId)
                );

                setRefreshKey((prev) => prev + 1);

                setTriggerRefresh((prev) => prev + 1);
                console.log('✅ Refresh triggered after delete');

                Swal.fire({
                    title: t('general.success'),
                    text: 'Rol sorumlusu başarıyla kaldırıldı',
                    icon: 'success',
                    timer: 2000,
                });
            }
        } catch (error) {
            console.error('Error deleting role approver:', error);
            Swal.fire({
                title: t('general.error'),
                text: 'Rol sorumlusu kaldırılırken hata oluştu',
                icon: 'error',
            });
        } finally {
            fetchExistingRoleApprovers(role?.roleId);
        }
    };

    const chipList = useMemo(() => {
        return roleManagers.map((user, index) => (
            <Chip
                key={`chip-${user.approverUserId || user.userRef}-${refreshKey}-${index}`}
                label={`${user.userName || 'Unknown'}${user.email ? ` (${user.email})` : ''}`}
                onDelete={() => handleRoleManagerDelete(user)}
                color="primary"
                variant="outlined"
                sx={{ margin: 0.5 }}
            />
        ));
    }, [roleManagers, refreshKey, handleRoleManagerDelete]);

    const handleClose = () => {
        setRoleManagers([]);
        onClose();
    };

    useEffect(() => {
        if (open) {
            initializeModal();
        }
    }, [open, role, triggerRefresh]);

    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: { borderRadius: 2 },
                },
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            {t('roleManagement.editRoleTitle')}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('roleManagement.roleInformation')}
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, mb: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>{t('authorizationManagement.roleName')}:</strong> {role?.roleNameKey}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            <strong>Role ID:</strong> {role?.roleId}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />
                <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('roleManagement.roleManagers')}
                        {isLoadingExisting && <CircularProgress size={16} sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {t('roleManagement.roleManagersDescription')}
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            label={t('roleManagement.searchUsers')}
                            placeholder={t('roleManagement.searchUsersPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                setSearchTerm(newValue);

                                if (searchTimeout) {
                                    clearTimeout(searchTimeout);
                                }

                                if (newValue.length >= 3) {
                                    setShowWarning(false);
                                    const timeout = setTimeout(() => {
                                        fetchUsers(newValue.trim());
                                    }, 500);
                                    setSearchTimeout(timeout);
                                } else if (newValue.length > 0) {
                                    setShowWarning(true);
                                    setAllUsers([]);
                                } else {
                                    setShowWarning(false);
                                    setAllUsers([]);
                                }
                            }}
                            onKeyDown={handleKeyPress}
                            variant="outlined"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {isLoadingUsers && <CircularProgress size={20} />}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        />
                        {showWarning && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                {t('roleManagement.minCharacterWarning')}
                            </Alert>
                        )}
                    </Box>

                    {allUsers.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('roleManagement.searchResults')}: {allUsers.length} {t('roleManagement.usersFound')}
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: 300,
                                    overflow: 'auto',
                                    border: '1px solid rgba(0, 0, 0, 0.12)',
                                    borderRadius: 1,
                                    p: 1,
                                }}
                            >
                                {allUsers.map((user) => (
                                    <Box
                                        key={user.userRef}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 1,
                                            borderRadius: 1,
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body1">{user.userName || 'Unknown'}</Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                if (!roleManagers.find((rm) => rm.userRef === user.userRef)) {
                                                    handleAddRoleApprover(user);
                                                }
                                            }}
                                            disabled={roleManagers.some((rm) => rm.userRef === user.userRef)}
                                        >
                                            {roleManagers.some((rm) => rm.userRef === user.userRef)
                                                ? t('roleManagement.alreadySelected')
                                                : t('general.add')}
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {roleManagers.length > 0 && (
                        <Box key={`role-managers-${refreshKey}`} sx={{ mt: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('roleManagement.selectedRoleManagers')}: {roleManagers.length}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{chipList}</Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};
